import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import parentApi from "@/lib/parentApi";
import ChatThread from "@/components/ChatThread";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

export default function ParentChat() {
  const { studentId } = useParams();
  const nav = useNavigate();
  const backTo = `/parent/student/${studentId}`;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data: d } = await parentApi.get(`/parent/threads/${studentId}`);
      setData(d);
    } catch (e) {
      if (e.response?.status === 401) { nav("/parent/login"); return; }
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [studentId, nav]);

  useEffect(() => {
    if (!localStorage.getItem("vlog_parent_token")) { nav("/parent/login"); return; }
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [load, nav]);

  const send = async (body, attachment) => {
    try {
      const { data: msg } = await parentApi.post(`/parent/threads/${studentId}`, { body, attachment: attachment || undefined });
      setData((d) => ({ ...d, messages: [...(d?.messages || []), msg] }));
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
      return false;
    }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-slate-50" data-testid="parent-chat-page">
      <header className="bg-white border-b border-slate-200 text-slate-900 px-4 py-3 shrink-0">
        <div className="max-w-md mx-auto w-full flex items-center gap-3">
          <button onClick={() => nav(backTo)} data-testid="parent-chat-back" className="h-9 w-9 rounded-lg border border-slate-200 bg-white text-slate-700 inline-flex items-center justify-center hover:bg-slate-100">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="min-w-0">
            <div className="font-display font-semibold leading-tight truncate" data-testid="parent-chat-student">{data?.student_name || "…"}</div>
            <div className="text-[11px] text-slate-500 truncate">
              {[data?.class_name, data?.teacher_name && `Teacher: ${data.teacher_name}`].filter(Boolean).join(" · ") || "Your reply goes to the class teacher"}
            </div>
          </div>
        </div>
      </header>
      <div className="flex-1 min-h-0 max-w-md mx-auto w-full">
        {loading ? (
          <div className="h-full flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <ChatThread
            messages={data?.messages || []}
            onSend={send}
            mySenderType="parent"
            emptyText="Start the conversation — your message goes straight to the class teacher."
          />
        )}
      </div>
    </div>
  );
}
